/* ==========================================================================
   Pack Builder — funnel events for GrowthBook
   --------------------------------------------------------------------------
   Purchase and checkout_started already reach GrowthBook from the checkout web
   pixel (scripts/growthbook-web-pixel.js). Neither can see the step in between
   that the builder is actually judged on: whether the visitor ever filled the
   pack. Without it a builder test can only be read at the very bottom of the
   funnel, where the signal is thinnest.

   This file adds that step, plus two scroll marks — the page's other complaint
   is scroll length, so it is worth knowing whether an arm gets people further
   down the grid.

     pack_complete   once per page, when the visitor fills the pack HERE
     builder_scroll  once each at 50% and 90% of the document

   ARM-AGNOSTIC ON PURPOSE. It loads for every visitor to the acquisition
   builder, in both arms and on both device classes, and it never reads a
   feature flag — that is what makes the numbers comparable. GrowthBook
   attributes each event to whichever arm the visitor was already bucketed into.

   ACQUISITION ONLY. sections/pack-builder.liquid ships this file on the
   build-your-pack template alone. The same section renders edit-your-pack,
   where a subscriber's pack arrives already full and the builder enables the
   CTA at init — every such visit would have logged a pack_complete that no
   visitor action produced, attributed to whatever arm that device was bucketed
   into months earlier during acquisition.

   WHAT "COMPLETE" MEANS HERE. Two things the obvious implementation gets wrong:

     1. It has to be a build that happened on THIS pageview. A pack survives in
        localStorage and is re-synced from the cart, so a visitor who filled one
        yesterday and comes back to browse arrives complete; so does anyone
        returning via ?build_bundle= prefill links. Counting those measures
        revisits, not builds. So completion is only armed after the visitor
        actually touches the builder.
     2. The pack is full when the BUILDER says it is, not at cupTotal ===
        cupLimit. A 12oz cup fills half a slot, so the builder treats half a cup
        short as checkout-ready (see updateTotalsAndCups) — a legitimately
        finished 5.5/6 pack must count, and must report pack_size 6, not 5.

   Both numbers come off window.packBuilderInstance (pack-builder.js), which is
   the source of truth for cupTotal and cupLimit. Reading them from the DOM is
   not equivalent: .pb-summary__cup-total-value has three writers with two
   different meanings — setCupLimitDisplay() writes the LIMIT to every instance
   while updateTotalsAndCups() writes the running TOTAL into the panel's copy —
   so parsing it yields the limit or the count depending on what happened last.

   Events land in the warehouse's `events` table (exposures are in
   experiment_views; only custom gbEvents calls go to `events`). window.gbEvents
   is a plain array until the SDK instantiates and replaces it with a pusher, so
   pushing before init is safe and the queue is drained on boot. On a page with
   no live test the SDK never boots, the array is never drained, and these
   events are simply never sent — which is correct, since there would be nothing
   to attribute them to.

   ATTACHING THE METRICS. Do NOT create the GrowthBook fact metrics for these
   until real events have landed and the fact table's columns have been
   refreshed. A metric pointed at a column GrowthBook has not yet discovered is
   accepted by the API and then fails every snapshot refresh, taking the
   unrelated metrics down with it. Ship this, let it fill, then add them.
   ========================================================================== */
(function () {
  "use strict";

  // Section scripts re-run on every settings change in the theme editor, which
  // would stack observers and listeners and re-fire every event per re-render.
  if (window.Shopify && window.Shopify.designMode) return;
  if (window.__pbBuilderEvents) return;
  window.__pbBuilderEvents = true;

  function emit(eventName, properties) {
    try {
      window.gbEvents = window.gbEvents || [];
      window.gbEvents.push({ eventName: eventName, properties: properties || {} });
    } catch (e) {
      /* analytics must never break the builder */
    }
  }

  var mobileMql = window.matchMedia("(max-width: 1023px)");

  function deviceClass() {
    return mobileMql.matches ? "mobile" : "desktop";
  }

  /* --------------------------------------------------------- pack completion

     Once per page load. A visitor who fills a pack, removes a cup and refills
     it has completed one build, not two — counting the refill would reward an
     arm for churn. */
  var completeSent = false;
  var interacted = false;
  var sawIncomplete = false;
  var observer = null;

  function checkComplete() {
    if (completeSent) return;

    var pb = window.packBuilderInstance;
    if (!pb) return;

    var total = Number(pb.cupTotal);
    var limit = Number(pb.cupLimit);
    if (!Number.isFinite(total) || !Number.isFinite(limit) || limit <= 0) return;

    // Short of the builder's own threshold (half-cup included), so not full.
    if (total < limit - 0.5) {
      // Only counts once the visitor is engaged. An empty pack seen during
      // hydration says nothing about them; an incomplete pack seen after they
      // started touching it is the beginning of a build.
      if (interacted) sawIncomplete = true;
      return;
    }

    // Full — but only a build if we watched it fill HERE. Without this a pack
    // restored from localStorage, re-synced from the cart, or dropped in whole
    // by a ?preset= link is already complete before the visitor does anything,
    // and their first idle tap would post a completion they did not perform.
    if (!interacted || !sawIncomplete) return;

    completeSent = true;
    if (observer) observer.disconnect();

    emit("pack_complete", {
      device: deviceClass(),
      // 6 or 9 — the guardrail on pack mix reads off this.
      pack_size: limit,
    });
  }

  function noteInteraction() {
    interacted = true;
    checkComplete();
  }

  function start() {
    var root = document.querySelector("pack-builder") || document.body;

    // pointerdown covers mouse and touch; keydown keeps keyboard users counted.
    root.addEventListener("pointerdown", noteInteraction, { passive: true });
    root.addEventListener("keydown", noteInteraction, { passive: true });

    watchQuickview();

    // The totals live in the summary drawer, so watch the two slots it mounts
    // into rather than the whole builder — the 41-card grid re-renders on every
    // add and remove, and none of that churn is the signal.
    var scopes = [];
    ["pb-summary-slot-mobile", "pb-summary-slot-desktop"].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) scopes.push(el);
    });
    if (!scopes.length) scopes.push(root);

    // MutationObserver callbacks are already batched per microtask, so there is
    // nothing for a debounce to coalesce — and a timer would only open a window
    // in which a completed pack goes unrecorded because the visitor navigated.
    observer = new MutationObserver(checkComplete);
    scopes.forEach(function (el) {
      observer.observe(el, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["disabled", "class"],
      });
    });
  }

  /* ---------------------------------------------------------- cup overlay

     Tapping a card's photo or name opens the cup overlay (openQuickview in
     pack-builder.js: three photos, macros, Add, Details / What's Inside /
     Nutrition). It has shipped for months with nothing measuring whether a
     visitor ever opens it, so a redesign of that overlay could not be judged
     against today's. Two events, both delegated at the document because the
     grid re-renders on every add and remove and the overlay lives outside the
     <pack-builder> root:

       quickview_open  each time the overlay opens from a card
       quickview_add   each time the overlay's Add button is used

     Opens are not deduped on purpose: how many cups a visitor inspects before
     adding is itself the signal. Read them in GrowthBook as a proportion of
     users (>= 1 open) or as a count per user. */
  var OPENERS = ".pb__product-quickview, .pb__product-quickview-title";
  var OVERLAY_ADD = ".quickview-modal__controls-atp";

  function cardProductId(el) {
    var card = el.closest(".pb__product-container");
    return (card && card.getAttribute("data-product-id")) || undefined;
  }

  function onDocumentClick(e) {
    var target = e.target;
    if (!target || typeof target.closest !== "function") return;

    var opener = target.closest(OPENERS);
    if (opener) {
      emit("quickview_open", {
        device: deviceClass(),
        // photo vs name — tells a redesign which affordance is doing the work.
        source: opener.classList.contains("pb__product-quickview") ? "photo" : "name",
        product_id: cardProductId(opener),
      });
      return;
    }

    if (target.closest(OVERLAY_ADD)) {
      emit("quickview_add", { device: deviceClass() });
    }
  }

  function watchQuickview() {
    document.addEventListener("click", onDocumentClick, { passive: true });
  }

  /* ------------------------------------------------------------ scroll marks

     Two marks, not four, and each fires once — the builder is a high-traffic
     page and the free tier's event cap is worth respecting. Depth is measured
     against the scrollable distance rather than document height so a short
     viewport and a tall one mean the same thing by it. */
  var marks = [50, 90];
  var sent = {};

  // A restored scroll position (back-navigation, #category deep link) would
  // otherwise post both marks for a visitor who has not scrolled at all. Only
  // credit depth the visitor actually travelled past their entry point.
  var entryPct = 0;

  function currentPct() {
    var doc = document.documentElement;
    var scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return null;
    return ((window.pageYOffset || 0) / scrollable) * 100;
  }

  function onScroll() {
    var pct = currentPct();
    if (pct === null) return;

    var outstanding = false;

    for (var i = 0; i < marks.length; i++) {
      var mark = marks[i];
      if (sent[mark]) continue;

      // Already below the visitor's entry depth: retire it unemitted. They did
      // not travel this far, they arrived past it.
      if (mark <= entryPct) {
        sent[mark] = true;
        continue;
      }

      if (pct >= mark) {
        sent[mark] = true;
        emit("builder_scroll", { depth: mark, device: deviceClass() });
        continue;
      }

      outstanding = true;
    }

    // Nothing left to watch for.
    if (!outstanding) window.removeEventListener("scroll", onScroll);
  }

  function anchorDepth() {
    var pct = currentPct();
    if (pct !== null) entryPct = pct;
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("load", anchorDepth);
  window.addEventListener("pageshow", anchorDepth);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
